import type { CalendarDate } from '@internationalized/date';

import type { DateRangeValue } from '../types';

const calendarTitle = (title: string): string => {
  return `${title.slice(0, 3)} ${title.split(' ')[1]}`;
};

const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const calendarTitleRange = (visibleRange: DateRangeValue<CalendarDate>): [string, string] => {
  const startTitle = visibleRange.start ? `${months[visibleRange.start.month - 1]} ${visibleRange.start.year}` : '';
  const endTitle = visibleRange.end ? `${months[visibleRange.end.month - 1]} ${visibleRange.end.year}` : '';

  return [startTitle, endTitle];
};

/**
 * Range type for time selectors, indicating which part of a date/time range
 * the selector represents.
 */
export type TimeRangeType = 'end' | 'single' | 'start';

/**
 * Granularity levels for date/time components.
 */
export type TimeGranularity = 'hour' | 'minute' | 'second';

/**
 * Default time values structure.
 */
export interface DefaultTimeValues {
  hour: number;
  minute: number;
  second: number;
}

/**
 * Calculates default time values based on range type and granularity.
 *
 * For range start and single selectors, defaults to beginning-of-day (00:00:00).
 * For range end selectors, defaults to end-of-day based on granularity:
 * - hour: 23:00:00
 * - minute: 23:59:00
 * - second: 23:59:59
 *
 * @param rangeType - Which part of a range this selector represents
 * @param granularity - The precision level for time selection
 * @returns Object with hour, minute, and second default values
 */
const getTimeRangeDefaults = (
  rangeType: TimeRangeType = 'single',
  granularity: TimeGranularity = 'minute',
): DefaultTimeValues => {
  // For 'start' and 'single', always use beginning-of-day
  if (rangeType !== 'end') {
    return { hour: 0, minute: 0, second: 0 };
  }

  // For 'end' range, calculate end-of-day based on granularity
  const endOfDay = { hour: 23, minute: 59, second: 59 };

  switch (granularity) {
    case 'hour': {
      return { ...endOfDay, minute: 0, second: 0 };
    }
    case 'minute': {
      return { ...endOfDay, second: 0 };
    }
    case 'second': {
      return endOfDay;
    }
    default: {
      return { hour: 0, minute: 0, second: 0 };
    }
  }
};

export { calendarTitle, calendarTitleRange, getTimeRangeDefaults };
